import type { AdminCrewReportMonthsOnBoardChartProps } from "../../models/admin-crew-report.models";
import {
  formatAdminCrewReportPeopleCount,
  formatAdminCrewReportMonthsOnBoardPercentage,
  getAdminCrewReportMonthsOnBoardAccessibleSummary,
  getAdminCrewReportMonthsOnBoardBarHeight,
} from "../../utils/admin-crew-report-statistics.utils";
import {
  ADMIN_CREW_REPORT_MONTHS_ON_BOARD_BUCKET_LABELS,
  ADMIN_CREW_REPORT_STATISTICS_CARD_HEIGHT_CLASS_NAME,
  ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME,
  ADMIN_CREW_REPORT_STATISTICS_VIEW_LABELS,
} from "../../utils/admin-crew-report.constants";

const AdminCrewReportMonthsOnBoardChart = ({ statistics, view }: AdminCrewReportMonthsOnBoardChartProps) => {
  const viewLabels = ADMIN_CREW_REPORT_STATISTICS_VIEW_LABELS[view];
  const accessibleSummary = getAdminCrewReportMonthsOnBoardAccessibleSummary(statistics.buckets, view);
  const maximumPercentage = Math.max(0, ...statistics.buckets.map((bucket) => bucket.percentage));

  return (
    <article
      data-test="admin-crew-report-months-on-board-card"
      data-testid="admin-crew-report-months-on-board-card"
      className={`${ADMIN_CREW_REPORT_STATISTICS_CARD_HEIGHT_CLASS_NAME} border border-[#D8E6E3] bg-white px-4 pb-0 pt-[11px] dark:border-gray-700 dark:bg-gray-800`}
    >
      <h2 className={ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME}>Months On Board</h2>

      <div
        role="img"
        aria-label={`Months On Board distribution. ${accessibleSummary}. ${formatAdminCrewReportPeopleCount(statistics.excludedCrewCount, view)} excluded because a sign-on date is missing.`}
        className="mt-3"
      >
        {statistics.categorizedCrewCount === 0 ? (
          <div className="flex h-[196px] items-center justify-center px-4 text-center text-sm text-[#65706F] dark:text-gray-300">
            No {viewLabels.personLabel} with valid sign-on dates are available.
          </div>
        ) : (
          <div
            data-testid="admin-crew-report-months-on-board-layout"
            aria-hidden="true"
            className="admin-crew-report-months-on-board-layout flex h-[200px] flex-col pt-5"
          >
            <div className="grid min-h-0 flex-1 grid-cols-4 gap-2 border-b border-[#8AA6A1] dark:border-gray-500 sm:gap-3">
              {statistics.buckets.map((bucket) => {
                const barHeight = getAdminCrewReportMonthsOnBoardBarHeight(bucket.percentage, maximumPercentage);

                return (
                  <div key={bucket.key} className="relative min-w-0">
                    <span
                      className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold text-[#24302E] dark:text-gray-100"
                      style={{ bottom: `calc(${barHeight}% + 0.25rem)` }}
                    >
                      {formatAdminCrewReportMonthsOnBoardPercentage(bucket)}
                    </span>
                    <span
                      data-test={`admin-crew-report-months-on-board-bar-${bucket.key}`}
                      data-testid={`admin-crew-report-months-on-board-bar-${bucket.key}`}
                      className="absolute bottom-0 left-1/2 w-8 -translate-x-1/2 bg-blueLine dark:bg-sky-400 sm:w-10"
                      style={{
                        height: `${barHeight}%`,
                        minHeight: bucket.count > 0 ? "2px" : undefined,
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <div
              data-testid="admin-crew-report-months-on-board-labels"
              className="mt-1 grid h-2 grid-cols-4 gap-2 sm:gap-3"
            >
              {statistics.buckets.map((bucket) => (
                <div
                  key={bucket.key}
                  className="flex h-2 items-start justify-center whitespace-nowrap px-0.5 text-center text-[10px] font-semibold leading-3 text-[#475351] dark:text-gray-300 sm:text-[11px]"
                >
                  {ADMIN_CREW_REPORT_MONTHS_ON_BOARD_BUCKET_LABELS[bucket.key]}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

export default AdminCrewReportMonthsOnBoardChart;
